/**
 * 민원 관리 서비스
 */
import { db } from '../firebase/config';
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc,
  query, where, orderBy, serverTimestamp,
} from 'firebase/firestore';

const COL = 'complaint_logs';

/**
 * 민원 카테고리 enum — 담당자 확정안 (2026-04-23)
 * 저장: complaint_logs.category
 */
export const COMPLAINT_CATEGORIES = {
  schedule_check: '일정 확인',
  schedule_change: '일정 변경',
  rematch: '재매칭 요청',
  cancel_request: '취소 요청',
  customer_dissatisfaction: '고객 불만',
  payment_overdue: '미수 결제',
  system_notice: '시스템 알림',
};

/** 운영자가 주의 깊게 모니터링해야 하는 3종 */
export const MONITORING_CATEGORIES = ['schedule_change', 'rematch', 'final_cancel'];

/** 파트너 자동 정지 임계값 (누적 벌점) */
export const AUTO_SUSPEND_THRESHOLD = 15;

// ───── 부동산: 내 고객 관련 민원 조회 ─────
export const getComplaintsByRealtor = async (realtorId) => {
  const q = query(
    collection(db, COL),
    where('sourceRealtorId', '==', realtorId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    date: d.data().createdAt?.toDate?.()?.toLocaleString('ko-KR') || '',
  }));
};

// ───── 제휴업체: 내 민원 조회 ─────
export const getComplaintsByPartner = async (partnerId) => {
  if (!partnerId) return [];
  try {
    const q = query(collection(db, COL), where('partnerId', '==', partnerId));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      date: d.data().createdAt?.toDate?.()?.toLocaleDateString('ko-KR') || '',
    })).sort((a, b) => {
      const ta = a.createdAt?.seconds || 0;
      const tb = b.createdAt?.seconds || 0;
      return tb - ta;
    });
  } catch {
    return [];
  }
};

// ───── 관리자: 전체 민원 조회 ─────
export const getAllComplaints = async (filters = {}) => {
  const q = query(collection(db, COL), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  let results = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    createdAt: d.data().createdAt?.toDate?.()?.toISOString().slice(0, 10) || '',
  }));

  if (filters.status && filters.status !== 'all') {
    results = results.filter((r) => r.status === filters.status);
  }

  return results;
};

// ───── 민원 등록 ─────
// - category: 서비스 카테고리(moving/internet_tv 등) — 기존 호환
// - complaintCategory: 민원 분류(schedule_check/schedule_change/rematch/cancel_request/customer_dissatisfaction/payment_overdue/system_notice)
export const createComplaint = async (data) => {
  const ref = await addDoc(collection(db, COL), {
    serviceRequestId: data.serviceRequestId || null,
    partnerId: data.partnerId || null,
    customerId: data.customerId || null,
    sourceRealtorId: data.sourceRealtorId || null,
    customerName: data.customerName || '',
    partnerName: data.partnerName || '',
    type: data.type || '기타',
    category: data.category || '',
    complaintCategory: data.complaintCategory || '',
    summary: data.summary || '',
    content: data.content || '',
    status: 'pending',
    createdAt: serverTimestamp(),
  });
  return ref.id;
};

// ───── 민원 상태 변경 + 후속 조치 (벌점 포함) ─────
export const updateComplaint = async (id, data) => {
  const patch = {
    status: data.status,
    followUpMemo: data.followUpMemo || '',
    followUpBy: data.followUpBy || '',
    followUpAt: serverTimestamp(),
  };
  if (data.complaintCategory) patch.complaintCategory = data.complaintCategory;
  // 벌점 부여 (운영자 HQ 액션)
  if (typeof data.penalty === 'number' && data.penalty > 0) {
    patch.penalty = data.penalty;
    patch.penaltyReason = data.penaltyReason || '';
    patch.penaltyAt = serverTimestamp();
  }
  await updateDoc(doc(db, COL, id), patch);
};

// ───── 관리자: 요청 모니터링 카테고리 3종만 조회 ─────
export const getMonitoringComplaints = async () => {
  const q = query(
    collection(db, COL),
    where('complaintCategory', 'in', MONITORING_CATEGORIES)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    createdAt: d.data().createdAt?.toDate?.()?.toISOString().slice(0, 10) || '',
  })).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
};

// ───── 특정 파트너의 민원 집계 (불만 현황 카드용) ─────
export const getComplaintSummaryByPartner = async (partnerId) => {
  const list = await getComplaintsByPartner(partnerId);
  const summary = {
    total: list.length,
    pending: 0,
    processing: 0,
    resolved: 0,
    dismissed: 0,
    penaltyTotal: 0,
    byCategory: {},
    recent: list.slice(0, 5),
  };
  list.forEach((c) => {
    if (c.status && summary[c.status] !== undefined) summary[c.status] += 1;
    if (typeof c.penalty === 'number') summary.penaltyTotal += c.penalty;
    const key = c.category || 'unknown';
    summary.byCategory[key] = (summary.byCategory[key] || 0) + 1;
  });
  return summary;
};
